import { Button, Dialog, DialogActions, DialogContent, Grid, IconButton, InputAdornment, TextField, Tooltip } from '@material-ui/core';
import { ArrowBackSharp, CalendarToday, Close, CloudDownload, DonutLarge, Edit, Layers, LocalAtm, Timeline, TrendingUp } from '@material-ui/icons';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { downloadIllustrationReport } from '../../helpers/PDFGenerator';
import { IsNullOrEmpty } from '../../helpers/utility';
import { fetchPlanRequest, fetchPlansRequest, updatePlanRequest } from '../../store/plan/plan.actions';
import { getPlanDetail, getPlans } from '../../store/plan/plan.selector';
import TitleBar from '../base/TitleBar';

const round = (value) => {
    const parsed = parseFloat(value);
    if (!isFinite(parsed)) return 0;
    return Math.round(parsed * 100) / 100;
};

const shareOf = (part, total) => {
    const parsed = parseFloat(total);
    if (!isFinite(parsed) || parsed === 0) return 0;
    return round(part * 100 / parsed);
};

const sumBy = (rows, key) => (rows || []).reduce((total, row) => total + (parseFloat(row[key]) || 0), 0);

export default function PlanContainer() {
    const dispatch = useDispatch();
    const [isEdit, setEdit] = useState(false);
    const [open, setOpen] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState("");
    const [amount, setAmount] = useState(100);
    const [date, setDate] = useState(moment().format("YYYY-MM-DD"));

    const [planName, setPlanName] = useState("");
    const [withdrawReturns, setWithdrawReturns] = useState("");
    const [monthlyReturns, setMonthlyReturns] = useState("");
    const [reinvestment, setReinvestment] = useState("");
    const [liquidity, setLiquidity] = useState("");
    const [adminFees, setAdminFees] = useState("");
    const [duration, setDuration] = useState("");

    const [isValidated, setValidated] = useState(false);

    const validate = () => {
        if (IsNullOrEmpty(planName)) {
            return false;
        }

        if (IsNullOrEmpty(duration) || duration <= 0) {
            return false;
        }

        if (IsNullOrEmpty(withdrawReturns) || withdrawReturns <= 0) {
            return false;
        }

        if (IsNullOrEmpty(liquidity) || liquidity <= 0) {
            return false;
        }

        if (IsNullOrEmpty(adminFees) || adminFees <= 0) {
            return false;
        }

        if (IsNullOrEmpty(monthlyReturns) || monthlyReturns <= 0) {
            return false;
        }

        if (IsNullOrEmpty(reinvestment) || reinvestment <= 0) {
            return false;
        }
        return true;
    }

    const handleSave = () => {
        if (validate()) {
            dispatch(updatePlanRequest({
                id: selectedPlan,
                planName: planName,
                duration: duration,
                withdrawReturns: withdrawReturns,
                monthlyReturns: withdrawReturns * monthlyReturns / 100,
                reinvestment: withdrawReturns * reinvestment / 100,
                liquidity: withdrawReturns * liquidity / 100,
                adminFees: withdrawReturns * adminFees / 100,
            }))
        }
        else {
            setValidated(true);
        }
    }

    const closeEditor = () => {
        setEdit(false);
        setSelectedPlan("");
        setValidated(false);
    };

    useEffect(() => {
        dispatch(fetchPlansRequest());
    }, [])

    const { plans, planDetail } = useSelector((state) => {
        return {
            plans: getPlans(state),
            planDetail: getPlanDetail(state)
        }
    }, []);

    useEffect(() => {
        setEdit(false);
    }, [plans])

    const activePlan = plans.find((plan) => plan._id === selectedPlan);

    const durations = plans.map((plan) => parseFloat(plan.duration) || 0);
    const topInterest = plans.reduce((best, plan) => Math.max(best, parseFloat(plan.withdrawReturns) || 0), 0);
    const longestTenure = durations.length ? Math.max.apply(null, durations) : 0;
    const avgWithdrawShare = plans.length
        ? round(plans.reduce((total, plan) => total + shareOf(plan.monthlyReturns, plan.withdrawReturns), 0) / plans.length)
        : 0;

    const stats = [
        { key: "count", tone: "blue", icon: <Layers />, value: plans.length, label: "Active Plans", note: "Published" },
        { key: "interest", tone: "green", icon: <TrendingUp />, value: `${round(topInterest)}%`, label: "Top Monthly Interest", note: "Best in catalogue" },
        { key: "tenure", tone: "amber", icon: <Timeline />, value: `${longestTenure} mo`, label: "Longest Tenure", note: "Maximum lock-in" },
        { key: "share", tone: "red", icon: <DonutLarge />, value: `${avgWithdrawShare}%`, label: "Avg Net Withdraw", note: "Share of interest" },
    ];

    const illustration = Array.isArray(planDetail) ? planDetail : [];
    const totals = {
        interest: round(sumBy(illustration, "interest")),
        withdraw: round(sumBy(illustration, "withdrawalAmount")),
        reinvest: round(sumBy(illustration, "reinvest")),
        liquidity: round(sumBy(illustration, "liquidityFees")),
        fees: round(sumBy(illustration, "adminFees")),
    };

    return (
        <div className='plans-page'>
            <Dialog maxWidth="sm" fullWidth open={isEdit} onClose={closeEditor} PaperProps={{ className: 'plan-edit-dialog' }}>
                <div className="plan-edit-hero">
                    <span className="plan-edit-hero-icon"><Layers /></span>
                    <div className="plan-edit-hero-body">
                        <span>Plan configuration</span>
                        <h3>{planName || "Plan Information"}</h3>
                    </div>
                    <IconButton className="plan-edit-close" onClick={closeEditor}>
                        <Close />
                    </IconButton>
                </div>
                <DialogContent className="plan-edit-content">
                    <div className="plan-edit-note">
                        Returns below are expressed as a share of the monthly interest. Net withdraw, reinvestment,
                        liquidity and gas fees should add up to 100%.
                    </div>
                    <Grid container spacing={2} className="plan-edit-grid">
                        <Grid item xs={12} sm={12} md={8} lg={8}>
                            <TextField
                                variant='outlined'
                                placeholder='Plan Name'
                                label="Plan Name"
                                value={planName}
                                onChange={(e) => setPlanName(e.target.value)}
                                fullWidth
                                margin='dense'
                                error={isValidated && IsNullOrEmpty(planName)}
                                helperText={isValidated && IsNullOrEmpty(planName) ? "Plan Name should be valid" : ""}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={4} lg={4}>
                            <TextField
                                variant='outlined'
                                placeholder='Duration'
                                label="Duration"
                                value={duration}
                                onChange={(e) => setDuration(e.target.value)}
                                fullWidth
                                margin='dense'
                                InputProps={{
                                    endAdornment: <InputAdornment position='end'>
                                        Months
                                    </InputAdornment>
                                }}
                                error={isValidated && IsNullOrEmpty(duration)}
                                helperText={isValidated && (IsNullOrEmpty(duration) || parseFloat(duration) <= 0) ? "Duration should be valid" : ""}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={6} lg={6}>
                            <TextField
                                variant='outlined'
                                placeholder='Withdraw Returns'
                                label="Monthly Interest"
                                value={withdrawReturns}
                                onChange={(e) => setWithdrawReturns(e.target.value)}
                                fullWidth
                                margin='dense'
                                InputProps={{
                                    endAdornment: <InputAdornment position='end'>
                                        %
                                    </InputAdornment>
                                }}
                                error={isValidated && IsNullOrEmpty(withdrawReturns)}
                                helperText={isValidated && (IsNullOrEmpty(withdrawReturns) || parseFloat(withdrawReturns) <= 0) ? "Withdraw Retuns should be valid" : ""}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={6} lg={6}>
                            <TextField
                                variant='outlined'
                                placeholder='Monthly Returns'
                                label="Net Withdraw"
                                value={monthlyReturns}
                                onChange={(e) => setMonthlyReturns(e.target.value)}
                                InputProps={{
                                    endAdornment: <InputAdornment position='end'>
                                        %
                                    </InputAdornment>
                                }}
                                fullWidth
                                margin='dense'
                                error={isValidated && IsNullOrEmpty(monthlyReturns)}
                                helperText={isValidated && (IsNullOrEmpty(monthlyReturns) || parseFloat(monthlyReturns) <= 0) ? "Monthly Retuns should be valid" : ""}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={4} lg={4}>
                            <TextField
                                variant='outlined'
                                placeholder='Reinvestment'
                                label="Reinvestment"
                                value={reinvestment}
                                onChange={(e) => setReinvestment(e.target.value)}
                                InputProps={{
                                    endAdornment: <InputAdornment position='end'>
                                        %
                                    </InputAdornment>
                                }}
                                fullWidth
                                margin='dense'
                                error={isValidated && IsNullOrEmpty(reinvestment)}
                                helperText={isValidated && (IsNullOrEmpty(reinvestment) || parseFloat(reinvestment) <= 0) ? "Reinvestment should be valid" : ""}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={4} lg={4}>
                            <TextField
                                variant='outlined'
                                placeholder='Liquidity'
                                label="Liquidity"
                                value={liquidity}
                                onChange={(e) => setLiquidity(e.target.value)}
                                InputProps={{
                                    endAdornment: <InputAdornment position='end'>
                                        %
                                    </InputAdornment>
                                }}
                                fullWidth
                                margin='dense'
                                error={isValidated && IsNullOrEmpty(liquidity)}
                                helperText={isValidated && (IsNullOrEmpty(liquidity) || parseFloat(liquidity) <= 0) ? "Liquidity should be valid" : ""}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={4} lg={4}>
                            <TextField
                                variant='outlined'
                                placeholder='Admin Fees'
                                label="Gas Fees"
                                value={adminFees}
                                onChange={(e) => setAdminFees(e.target.value)}
                                InputProps={{
                                    endAdornment: <InputAdornment position='end'>
                                        %
                                    </InputAdornment>
                                }}
                                fullWidth
                                margin='dense'
                                error={isValidated && IsNullOrEmpty(adminFees)}
                                helperText={isValidated && (IsNullOrEmpty(adminFees) || parseFloat(adminFees) <= 0) ? "Admin Fees should be valid" : ""}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions className="plan-edit-actions">
                    <Button className="plan-btn-ghost" onClick={closeEditor}>
                        Cancel
                    </Button>
                    <Button className="plan-btn-primary" variant='contained' onClick={() => handleSave()}>
                        Save Plan
                    </Button>
                </DialogActions>
            </Dialog>

            <TitleBar heading="Plans" secondary={""}
                icon={<Layers />}
                action={open ? <Button className='text-white' onClick={() => setOpen(false)} startIcon={<ArrowBackSharp />}>Back</Button> : undefined} />

            {
                !open &&
                <div className="plan-stats">
                    {
                        stats.map((stat) => (
                            <div className={`plan-stat plan-stat-${stat.tone}`} key={stat.key}>
                                <span className="plan-stat-icon">{stat.icon}</span>
                                <span className="plan-stat-body">
                                    <span className="plan-stat-value">{stat.value}</span>
                                    <span className="plan-stat-label">{stat.label}</span>
                                    <span className="plan-stat-note">{stat.note}</span>
                                </span>
                            </div>
                        ))
                    }
                </div>
            }

            <div className="plans-content">
                {
                    open ?
                        <div className="plan-calculator-workspace">
                            <div className="calculator-heading">
                                <div className="calculator-heading-main">
                                    <span className="plan-kicker">Illustration workspace</span>
                                    <h2>Plan calculator</h2>
                                    <p>Model projected returns using a contribution amount and settlement date.</p>
                                </div>
                                <div className="calculator-summary">
                                    <small>Selected plan</small>
                                    <strong>{(activePlan && activePlan.planName) || 'Plan'}</strong>
                                    {activePlan && <span className="plan-chip">{activePlan.duration} months</span>}
                                </div>
                            </div>

                            <Grid container spacing={2} className="calculator-controls">
                                <Grid item md={4} lg={4} sm={12} xs={12}>
                                    <TextField
                                        onChange={(e) => {
                                            setAmount(e.target.value);
                                            dispatch(fetchPlanRequest({
                                                id: selectedPlan,
                                                amount: e.target.value,
                                                date: date
                                            }))
                                        }}
                                        fullWidth
                                        variant='outlined'
                                        value={amount}
                                        margin='dense'
                                        label="Amount"
                                        InputProps={{
                                            endAdornment: <InputAdornment position='end'>USD</InputAdornment>
                                        }}
                                    />
                                </Grid>
                                <Grid item md={4} lg={4} sm={12} xs={12}>
                                    <TextField
                                        onChange={(e) => {
                                            setDate(e.target.value);
                                            dispatch(fetchPlanRequest({
                                                id: selectedPlan,
                                                amount: amount,
                                                date: e.target.value
                                            }))
                                        }}
                                        fullWidth
                                        type='date'
                                        variant='outlined'
                                        defaultValue={moment().format("YYYY-MM-DD")}
                                        value={date}
                                        margin='dense'
                                        label="Start Date"
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                    />
                                </Grid>
                                <Grid item md={4} lg={4} sm={12} xs={12}>
                                    <Button onClick={() => {
                                        downloadIllustrationReport(planDetail)
                                    }} className="plan-btn-primary calculator-download" fullWidth startIcon={<CloudDownload />}>
                                        Download Report
                                    </Button>
                                </Grid>
                            </Grid>

                            {
                                illustration.length > 0 &&
                                <div className="calculator-totals">
                                    <div className="calculator-total">
                                        <span>Instalments</span>
                                        <strong>{illustration.length}</strong>
                                    </div>
                                    <div className="calculator-total calculator-total-green">
                                        <span>Interest Gained</span>
                                        <strong>{totals.interest} USD</strong>
                                    </div>
                                    <div className="calculator-total calculator-total-blue">
                                        <span>Net Withdraw</span>
                                        <strong>{totals.withdraw} USD</strong>
                                    </div>
                                    <div className="calculator-total">
                                        <span>Reinvestment</span>
                                        <strong>{totals.reinvest} USD</strong>
                                    </div>
                                    <div className="calculator-total">
                                        <span>Liquidity</span>
                                        <strong>{totals.liquidity} USD</strong>
                                    </div>
                                    <div className="calculator-total calculator-total-red">
                                        <span>Gas Fees</span>
                                        <strong>{totals.fees} USD</strong>
                                    </div>
                                </div>
                            }

                            <div className="calculator-table-wrap">
                                <table className='table table-striped'>
                                    <thead>
                                        <tr>
                                            <th>S.No</th>
                                            <th>Date</th>
                                            <th>Deposit Amount</th>
                                            <th>Interest Gained</th>
                                            <th>Net Withdraw</th>
                                            <th>Gas Fees</th>
                                            <th>Liquidity</th>
                                            <th>Reinvestment</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {illustration.map((s, index) => {
                                            return <tr key={index}>
                                                <td><span className="plan-row-index">{index + 1}</span></td>
                                                <td>{s.date}</td>
                                                <td>{s.amount} USD</td>
                                                <td className="plan-cell-green">{s.interest} USD</td>
                                                <td className="plan-cell-strong">{s.withdrawalAmount} USD</td>
                                                <td>{s.adminFees} USD</td>
                                                <td>{s.liquidityFees} USD</td>
                                                <td>{s.reinvest} USD</td>
                                            </tr>
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {
                                illustration.length === 0 &&
                                <div className="plan-empty">
                                    <LocalAtm />
                                    <strong>No illustration yet</strong>
                                    <span>Enter an amount and a start date to project this plan's returns.</span>
                                </div>
                            }
                        </div>
                        :
                        <Grid container spacing={2} className="plan-grid">
                            {
                                plans.map((s, index) => {
                                    const split = [
                                        { key: "withdraw", label: "Net Withdraw", value: Math.ceil(shareOf(s.monthlyReturns, s.withdrawReturns)) },
                                        { key: "reinvestment", label: "Reinvestment", value: shareOf(s.reinvestment, s.withdrawReturns) },
                                        { key: "liquidity", label: "Liquidity", value: shareOf(s.liquidity, s.withdrawReturns) },
                                        { key: "fees", label: "Gas Fees", value: shareOf(s.adminFees, s.withdrawReturns) },
                                    ];
                                    const splitTotal = split.reduce((total, item) => total + item.value, 0) || 1;
                                    return <Grid item md={6} lg={6} sm={12} xs={12} key={s._id}>
                                        <div className="plan-card">
                                            <div className="plan-card-head">
                                                <span className="plan-card-index">{index + 1}</span>
                                                <div className="plan-card-title">
                                                    <strong>{s.planName}</strong>
                                                    <span className="plan-chip">{s.duration} months tenure</span>
                                                </div>
                                                <div className="plan-card-rate">
                                                    <strong>{round(s.withdrawReturns)}%</strong>
                                                    <span>Monthly interest</span>
                                                </div>
                                            </div>

                                            <div className="plan-split">
                                                <div className="plan-split-bar">
                                                    {split.map((item) => (
                                                        <span
                                                            key={item.key}
                                                            className={`plan-split-seg plan-split-${item.key}`}
                                                            style={{ width: `${item.value * 100 / splitTotal}%` }}
                                                            title={`${item.label} ${item.value}%`}
                                                        />
                                                    ))}
                                                </div>
                                                <ul className="plan-split-legend">
                                                    {split.map((item) => (
                                                        <li key={item.key}>
                                                            <span className={`plan-dot plan-split-${item.key}`} />
                                                            <span className="plan-split-label">{item.label}</span>
                                                            <strong>{item.value}%</strong>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>

                                            <div className="plan-card-actions">
                                                <Tooltip title="Edit plan configuration">
                                                    <Button className="plan-btn-ghost" startIcon={<Edit />} onClick={() => {
                                                        setSelectedPlan(s._id);
                                                        setPlanName(s.planName);
                                                        setDuration(s.duration);
                                                        setWithdrawReturns(s.withdrawReturns);
                                                        setMonthlyReturns(Math.ceil(parseFloat(s.monthlyReturns * 100 / s.withdrawReturns)));
                                                        setReinvestment(parseFloat(s.reinvestment * 100 / s.withdrawReturns));
                                                        setLiquidity(parseFloat(s.liquidity * 100 / s.withdrawReturns));
                                                        setAdminFees(parseFloat(s.adminFees * 100 / s.withdrawReturns));
                                                        setValidated(false);
                                                        setEdit(true)
                                                    }}>Edit</Button>
                                                </Tooltip>
                                                <Button className="plan-btn-primary" startIcon={<CalendarToday />} onClick={() => {
                                                    setSelectedPlan(s._id);
                                                    dispatch(fetchPlanRequest({
                                                        id: s._id,
                                                        amount: amount,
                                                        date: date
                                                    }))
                                                    setOpen(true);
                                                }}>Calculate</Button>
                                            </div>
                                        </div>
                                    </Grid>
                                })
                            }
                            {
                                plans.length === 0 &&
                                <Grid item xs={12}>
                                    <div className="plan-empty">
                                        <Layers />
                                        <strong>No plans configured</strong>
                                        <span>Investment plans published to members will show up here.</span>
                                    </div>
                                </Grid>
                            }
                        </Grid>
                }
            </div>
        </div>
    );
}
